<template>
  <main class="home">
    <!-- NEW: signed-in banner above the main container -->
    <section v-if="username" class="signed-in-banner">
      <div class="signed-in-banner__inner">
        <span>
          Signed in as <strong>{{ username }}</strong>
        </span>
        <router-link
          class="signed-in-banner__link"
          to="/account-page"
        >
          View account
        </router-link>
      </div>
    </section>

    <div class="container">
      <!-- Hero -->
      <section class="hero">
        <div class="hero__content">
          <h1>PathTracer</h1>
          <p class="tagline">Blockchain investigation tools</p>
          <p class="sub">
            Examine the flow of funds across <strong>BTC</strong>, <strong>ETH</strong>, and <strong>Tron</strong>.
          </p>

          <!-- Sticky-on-hero CTA (stays visible while hero is in view) -->
          <div class="cta sticky-on-hero">
            <router-link
              class="btn btn--primary"
              to="/AnalysisTool"
              data-track="cta_trace_click"
              :data-track-props="JSON.stringify({ source:'home_hero' })"
            >
              Trace by Transaction Hash
            </router-link>
          </div>

          <ul class="badges" aria-label="Supported networks">
            <li class="badge">BTC</li>
            <li class="badge">ETH</li>
            <li class="badge">TRON</li>
          </ul>
          <!-- removed the inline "Logged in as" here -->
        </div>

        <div class="hero__art" aria-hidden="true"></div>
      </section>

      <!-- Quick value props -->
      <section class="grid">
        <article class="card">
          <h3>Research likely destinations</h3>
          <p>Purpose-built for examining the likely flows of supported digital assets.</p>
        </article>

        <article class="card">
          <h3>Smart selection</h3>
          <p>Prompts when extra info is required and helps you choose the correct recipient.</p>
        </article>

        <article class="card">
          <h3>In-app help</h3>
          <p>FAQ and Support built in.</p>
          <!-- <router-link class="card__action" to="/support">Open Support</router-link> -->
        </article>
      </section>

      <!-- Guide CTA (kept tight and centered on wide screens) -->
      <section class="card card--guide">
        <div class="guide__content">
          <h2>Guide</h2>
          <p>
            New here? The quick start shows where to find transaction hashes and how to use the analysis tool correctly.
          </p>
        </div>
        <router-link
          class="btn btn--link guide__link"
          to="/BasicTutorial"
          data-track="open_tutorial_from_home"
          :data-track-props="JSON.stringify({ source:'home_guide_card' })"
        >
          See the Tutorial
        </router-link>
      </section>
    </div>
  </main>
</template>

<script>
import AuthService from '@/services/AuthService';

export default {
  name: 'HomePage',
  data() {
    return {
      username: null
    };
  },
  async mounted() {
    document.body.classList.add('route-home');

    const token = localStorage.getItem('token');
    if (token) {
      try {
        const isAuthed = await AuthService.isAuthenticated();
        if (isAuthed) {
          this.username = AuthService.getUsername();
        } else {
          this.username = null;
        }
      } catch {
        this.username = null;
      }
    }
  },
  beforeUnmount() {
    document.body.classList.remove('route-home');
  }
};
</script>

<style scoped>
/* Palette */
.home {
  --bg: #f7f9fc;
  --card: #ffffff;
  --ink: #1f2a37;
  --muted: #6b7280;
  --border: #e5e8ec;
  --accent: #35495e;
  --accent-2: #2c80b4;

  color: var(--ink);
  padding: 0 0 2.5rem; /* banner will handle its own padding */
  background: var(--bg);
}

/* NEW: signed-in banner */
.signed-in-banner {
  background: #e0f2fe;
  border-bottom: 1px solid #bfdbfe;
  padding: 0.4rem 1rem;
}
.signed-in-banner__inner {
  max-width: 1120px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  font-size: 0.85rem;
  color: #0b63a9;
}
.signed-in-banner__inner strong {
  font-weight: 600;
}
.signed-in-banner__link {
  font-weight: 600;
  text-decoration: underline;
  color: #0b63a9;
  white-space: nowrap;
}
.signed-in-banner__link:hover {
  text-decoration: none;
}

/* Constrain wide screens and center content */
.container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 1.25rem 1rem 0;
}

/* HERO */
.hero {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff, #f3f6fb);
  border: 1px solid var(--border);
  padding: clamp(1.25rem, 3vw, 2rem);
  display: grid;
}
.hero__content {
  max-width: 920px;
  margin: 0 auto;
  text-align: left;
}
.hero h1 {
  margin: 0;
  font-size: clamp(1.6rem, 1.8rem + 1vw, 2.4rem);
  line-height: 1.15;
  letter-spacing: .2px;
}
.tagline { margin: .25rem 0 0; color: var(--muted); }
.sub { margin: .5rem 0 0; font-size: 1.05rem; }

/* CTA */
.cta {
  display: flex;
  gap: .6rem;
  margin-top: .9rem;
  flex-wrap: wrap;
}
.sticky-on-hero { position: sticky; top: .75rem; z-index: 0; }

.badges {
  display: flex;
  gap: .5rem;
  margin: .9rem 0 0;
  padding: 0;
  list-style: none;
}
.badge {
  background: #eef2f7;
  border: 1px solid var(--border);
  color: #0b63a9;
  border-radius: 999px;
  padding: .35rem .6rem;
  font-size: .85rem;
  font-weight: 600;
}

/* Decorative hero art */
.hero__art {
  position: absolute;
  inset: -20% -10% auto auto;
  height: 280px;
  width: 520px;
  background:
    radial-gradient(280px 180px at 70% 30%, rgba(44,128,180,.18), transparent 60%),
    radial-gradient(240px 160px at 30% 70%, rgba(53,73,94,.14), transparent 60%);
  pointer-events: none;
}

/* BUTTONS */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  padding: .8rem 1rem;
  border-radius: 12px;
  font-weight: 700;
  text-decoration: none;
  transition: transform 60ms ease, filter 140ms ease, box-shadow 140ms ease;
  border: 1px solid transparent;
}
.btn:focus-visible { outline: 3px solid rgba(44,128,180,.35); outline-offset: 2px; }
.btn--primary {
  background: linear-gradient(180deg, var(--accent-2), #1f6d9a);
  color: #fff;
  box-shadow: 0 6px 16px rgba(31,109,154,.18);
}
.btn--primary:hover { filter: brightness(1.05); }
.btn--ghost {
  background: #fff;
  color: var(--accent);
  border-color: var(--border);
}
.btn--ghost:hover { filter: brightness(0.98); }
.btn--link {
  color: #0b63a9;
  text-decoration: underline;
  padding: .4rem .2rem;
  border-radius: 8px;
}
.btn--link:hover { text-decoration: none; }

/* GRID CARDS */
.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 10px 20px rgba(16,24,40,.06);
  padding: 1.2rem;
}
.card h3 { margin: 0 0 .35rem; font-size: 1.05rem; }
.card p { margin: 0; color: var(--muted); }

/* Card action link (support) */
.card__action {
  margin-top: .6rem;
  display: inline-block;
  color: #0b63a9;
  text-decoration: none;
  font-weight: 600;
}
.card__action:hover { text-decoration: underline; }

/* UPDATES / GUIDE */
.card--updates,
.card--guide {
  max-width: 920px;
  margin: 1rem auto 0;
}

.card__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: .5rem;
  margin-bottom: .5rem;
}
.card__header h2 { margin: 0; font-size: 1.15rem; }
.card__link {
  color: #0b63a9;
  text-decoration: none;
  font-weight: 600;
}
.card__link:hover { text-decoration: underline; }

.timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: .5rem;
}
.timeline li {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: .75rem;
  align-items: baseline;
  border-top: 1px dashed #e8ecf2;
  padding-top: .5rem;
}
.timeline li:first-child { border-top: none; padding-top: 0; }
.timeline time { color: var(--muted); font-size: .9rem; }
.timeline span { line-height: 1.5; }

/* GUIDE */
.card--guide {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: .75rem;
}
.card--guide p { margin: .25rem 0 0; color: var(--muted); }
.guide__link { justify-self: start; }

/* RESPONSIVE */
@media (max-width: 1100px) {
  .container { padding-inline: 0.75rem; }
}
@media (max-width: 960px) {
  .grid { grid-template-columns: 1fr; }
  .timeline li { grid-template-columns: 100px 1fr; }
}
@media (max-width: 520px) {
  .hero__art { display: none; }
  .timeline li { grid-template-columns: 1fr; }
  .timeline time { order: 2; }
  .timeline span { order: 1; }
  .card--guide { grid-template-columns: 1fr; }
  .guide__link { justify-self: start; }
  .cta { gap: .5rem; }

  .signed-in-banner__inner {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

<style>
/* Only active while HomePage is mounted */
.route-home #app > nav { z-index: 1000; }
.route-home,
.route-home #app,
.route-home #app > main { background: #f7f9fc; }

/* Belt & suspenders: make sure hero pieces never outrank the nav */
.route-home .home .hero,
.route-home .home .hero__content,
.route-home .home .cta { position: relative; z-index: 0; }
.route-home .home .hero__art { position: absolute; z-index: -1; }

/* If you still want the CTA non-sticky, force it here */
.route-home .home .sticky-on-hero {
  position: static !important;
  top: auto !important;
  z-index: auto !important;
}
</style>
